import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { MultiPin } from "@mapmycustomers/shared/types/map";
import {
  convertLatLngBoundsToPlatformBounds,
  isPlatformBoundsEmpty,
} from "@mapmycustomers/shared/util/geo/GeoService";

import { clearRecordsList, enterLassoMode, exitLassoMode } from "@app/scene/map/store/actions";
import { getMultiPin } from "@app/scene/map/store/mapMode/selectors";
import { getMapLassoPaths, getMapViewTool } from "@app/scene/map/store/selectors";
import { MapFetcherPayload } from "@app/scene/map/types/MapFetcher";
import { RootState } from "@app/store/rootReducer";
import GeoPath from "@app/types/GeoPath";

interface Props {
  clearRecordsList: typeof clearRecordsList;
  enterLassoMode: typeof enterLassoMode;
  exitLassoMode: typeof exitLassoMode;
  fetcher: (payload: MapFetcherPayload) => unknown;
  multiPin?: MultiPin;
  onUpdateLasso?: (multipolygon: LongLat[][]) => void;
  paths: Array<GeoPath>;
  tool?: MapTool;
}

const EntityLassoTool: React.FC<Props> = ({
  clearRecordsList,
  enterLassoMode,
  exitLassoMode,
  fetcher,
  multiPin,
  onUpdateLasso,
  paths,
  tool,
}) => {
  const isActive = tool === MapTool.LASSO;
  const isMultiPin = !!multiPin;

  useEffect(() => {
    if (isActive) {
      enterLassoMode();

      return () => {
        exitLassoMode();
      };
    }
  }, [enterLassoMode, exitLassoMode, isActive]);

  const [bounds, multipolygon] = useMemo(() => {
    if (isActive) {
      const bounds = new google.maps.LatLngBounds();
      const multipolygon: LongLat[][] = [];

      paths.forEach((path) => {
        multipolygon.push(
          path.map((item: google.maps.LatLng): LongLat => [item.lng(), item.lat()])
        );

        path.forEach((latlng) => {
          bounds.extend(latlng);
        });
      });
      onUpdateLasso?.(multipolygon);

      return [convertLatLngBoundsToPlatformBounds(bounds), multipolygon];
    }

    return [];
  }, [isActive, onUpdateLasso, paths]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    if (bounds && !isPlatformBoundsEmpty(bounds)) {
      fetcher({
        request: {
          lasso: multipolygon,
        },
      });
    } else if (!isMultiPin) {
      clearRecordsList();
    }
  }, [bounds, clearRecordsList, fetcher, isMultiPin, isActive, multipolygon]);

  return null;
};

const mapStateToProps = (state: RootState) => ({
  multiPin: getMultiPin(state),
  paths: getMapLassoPaths(state),
  tool: getMapViewTool(state),
});

const mapDispatchToProps = {
  clearRecordsList,
  enterLassoMode,
  exitLassoMode,
};

export default connect(mapStateToProps, mapDispatchToProps)(EntityLassoTool);
